import { Control, Controller } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import {
  SelectSize,
  Select,
  SelectType,
  ButtonSecondary,
  IconExpand,
  ButtonSize,
  IconLoader,
} from '@ui/src';
import { useAppSelector } from '@store';
import { CorporateQuestionnarieFormValues } from '@clients/hoc/corporate/types';
import { COFAndIndividualQuestionnarieFormValues } from '@clients/hoc/individual/types';
import InfoRow from '../common/info-row';
import {
  AccountsCurrenciesFormValues,
  NewApplicationStatus,
} from '../../../hoc/new-application/types';
import { CorporateApplicationStepOneTypes } from './types';
import StepPartBlock from '../common/step-part-block';
import StepOneBlockWrapper from './parts/step-one-block-wrapper';
import PepQuestionnarie from '../common/questionnarie/pep-questionnarie';
import GroupOfCompanies from './parts/group-of-companies';
import BusinessActivities from './parts/busniess-activites';
import CustomerWealth from './parts/customer-wealth';
import AccountCurrency from '../common/questionnarie/account-currency';
import AccountFlow from '../common/questionnarie/account-flow';
import ExpectedFundsOrigins from '../common/questionnarie/expected-funds-origins';
import CompanyAddress from './parts/address';
import CountryAndName from './parts/country-and-name';
import ContactDetails from './parts/contact-details';
import AdditionalInformation from './parts/additional-information';
import RegistrationInformation from './parts/registration-information';
import useStepOne from './hooks/use-step-one';

import commonStyles from '../common/styles.module.css';

const NewApplicationCorporateInfoStepOne = ({
  control,
  errors,
  countries,
  fields,
  sicCodes,
  countriesOfInterest,
  setValue,
  status,
  isEditable,
  applicationScheme,
  clearErrors,
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  questionnarieFields,
  currencies,
  requiredCurrencies,
  regulatedCompanyScheme,
  relationshipToTheCustomerDictionary,
  sourceOfWealth,
  baseCurrency,
  busniessActivities,
  customerMainCleints,
  salesChannels,
  questionnarieSetValue,
  customerSourceOfWealth,
  customerSourceOfFunds,
  companyLegalName,
  annualIncomes,
  totalIncomeError,
  setTotalIncomeError,
  typeOfAccounts,
  accountsCurrenciesValues,
  setAccountsCurrenciesValues,
  onDeleteAccountCurrencies,
  selectedAccountsCurrencyId,
  deleteAccountsCurrenciesInProgress,
  accountsFlowsValues,
  setAccountsFlowsValues,
  currenciesList,
  accountsCurrenciesIsLoading,
  accountsCurrenciesIsFetching,
  accountsFlowsIsLoading,
  accountsFlowsIsFetching,
  expectedFundsOriginsIsLoading,
  expectedFundsOriginsIsFetching,
  deleteExpectedFundsOriginsInProgress,
  onDeleteExpectedFundsOrigins,
  selectedExpectedFundsOriginsId,
  expectedFundsOriginsValues,
  setExpectedFundsOriginsValues,
  questionnarieIsLoading,
}: CorporateApplicationStepOneTypes) => {
  const { deliveryChannelsCorporate } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const {
    sicCodesList,
    setSicCodesList,
    countriesOfInterestList,
    setCountriesOfInterestList,
    requiredCurrenciesList,
    setRequiredCurrenciesList,
    groupedAccountsFlows,
    incomingExpectedFundsOfOrigins,
    outgoingExpectedFundsOfOrigins,
  } = useStepOne({
    accountsFlowsValues,
    expectedFundsOriginsValues,
    sicCodes,
    countriesOfInterest,
    requiredCurrencies,
  });

  let countriesOfInterestVisible = false;
  let deliveryChannelsVisible = false;

  if (
    !applicationScheme ||
    (applicationScheme && applicationScheme.countriesOfInterestActive) ||
    (applicationScheme && applicationScheme?.countriesOfInterestActive === null)
  ) {
    countriesOfInterestVisible = true;
  }

  if (
    !applicationScheme ||
    (applicationScheme && applicationScheme.deliveryChannelVisible)
  ) {
    deliveryChannelsVisible = true;
  }

  return (
    <div className={commonStyles.stepBlockWrapper}>
      <StepOneBlockWrapper
        title="Company profile"
        regulatedCompanyScheme={regulatedCompanyScheme}
      >
        <div className={commonStyles.stepBlockContent}>
          <CountryAndName
            control={control}
            countries={countries}
            errors={errors}
            status={status}
            clearErrors={clearErrors}
            isEditable={isEditable}
            regulatedCompanyScheme={regulatedCompanyScheme}
            questionnarieClearErrors={questionnarieClearErrors}
            questionnarieControl={questionnarieControl}
            questionnarieErrors={questionnarieErrors}
          />
          <CompanyAddress
            blockTitle="Registered address"
            countryIdName="registeredCountryId"
            control={control}
            countries={countries}
            status={status}
            isEditable={isEditable}
            clearErrors={clearErrors}
            errors={errors}
            cityName="registeredCity"
            postalCodeName="registeredPostalCode"
            address1Name="registeredAddress1"
            address2Name="registeredAddress2"
          />
          <CompanyAddress
            blockTitle="Business address"
            countryIdName="businessCountryId"
            control={control}
            countries={countries}
            status={status}
            isEditable={isEditable}
            clearErrors={clearErrors}
            errors={errors}
            cityName="businessCity"
            postalCodeName="businessPostalCode"
            address1Name="businessAddress1"
            address2Name="businessAddress2"
            checkboxName="businessAddressSameAsRegisteredAddress"
            fields={fields}
            withCheckbox
          />
          <CompanyAddress
            blockTitle="Correspondence address"
            countryIdName="correspondenceCountryId"
            control={control}
            countries={countries}
            status={status}
            isEditable={isEditable}
            clearErrors={clearErrors}
            errors={errors}
            cityName="correspondenceCity"
            postalCodeName="correspondencePostalCode"
            address1Name="correspondenceAddress1"
            address2Name="correspondenceAddress2"
            checkboxName="correspondenceAddressSameAsRegisteredAddress"
            fields={fields}
            withCheckbox
          />

          {regulatedCompanyScheme && (
            <ContactDetails
              questionnarieControl={questionnarieControl}
              questionnarieClearErrors={questionnarieClearErrors}
              status={status}
              isEditable={isEditable}
              questionnarieErrors={questionnarieErrors}
              questionnarieIsLoading={questionnarieIsLoading}
            />
          )}
          {applicationScheme &&
            (applicationScheme?.accountantDetailsActive ||
              applicationScheme?.auditedByExternalComplianceActive ||
              applicationScheme?.changesInManagementOrOwnershipActive ||
              applicationScheme?.complianceConsultantDetailsActive ||
              applicationScheme?.fcaNumberActive ||
              applicationScheme?.hmrcMlrNumberActive ||
              applicationScheme?.hmrcRegistrationExpiryDateActive ||
              applicationScheme?.icoNumberActive ||
              applicationScheme?.itProviderDetailsActive ||
              applicationScheme?.mlroDetailsActive ||
              applicationScheme?.numberOfAgentsActive ||
              applicationScheme?.numberOfBranchesActive ||
              applicationScheme?.onboardCorporateClientsActive ||
              applicationScheme?.onboardMsbActive ||
              applicationScheme?.paymentsForCharitiesActive ||
              applicationScheme?.payoutPartnerDetailsActive ||
              applicationScheme?.regulatoryInvestigationActive ||
              applicationScheme?.screeningActive ||
              applicationScheme?.transactionMonitoringActive ||
              applicationScheme?.typeOfBusinessLicenceActive ||
              applicationScheme?.visitedByAuthoritiesActive ||
              applicationScheme?.workingWithAnyOtherIPSPActive) && (
              <AdditionalInformation
                applicationScheme={applicationScheme}
                questionnarieControl={questionnarieControl}
                questionnarieClearErrors={questionnarieClearErrors}
                questionnarieErrors={questionnarieErrors}
                isEditable={isEditable}
                status={status}
                questionnarieFields={questionnarieFields}
                questionnarieIsLoading={questionnarieIsLoading}
              />
            )}
          <RegistrationInformation
            control={control}
            status={status}
            clearErrors={clearErrors}
            isEditable={isEditable}
            errors={errors}
            regulatedCompanyScheme={regulatedCompanyScheme}
            questionnarieControl={questionnarieControl}
            questionnarieClearErrors={questionnarieClearErrors}
            questionnarieErrors={questionnarieErrors}
            sicCodesList={sicCodesList}
            setSicCodesList={setSicCodesList}
            fields={fields}
            setValue={setValue}
            countriesOfInterestVisible={countriesOfInterestVisible}
            countriesOfInterestList={countriesOfInterestList}
            applicationScheme={applicationScheme}
            setCountriesOfInterestList={setCountriesOfInterestList}
            countries={countries}
            deliveryChannelsVisible={deliveryChannelsVisible}
            deliveryChannelsCorporate={deliveryChannelsCorporate}
            setRequiredCurrenciesList={setRequiredCurrenciesList}
            requiredCurrenciesList={requiredCurrenciesList}
            currencies={currencies}
          />
        </div>
      </StepOneBlockWrapper>
      {regulatedCompanyScheme && (
        <>
          <StepOneBlockWrapper title="Economic Profile" regulatedCompanyScheme>
            <div className={commonStyles.stepBlockContent}>
              <PepQuestionnarie
                questionnarieControl={
                  questionnarieControl as Control<
                    | CorporateQuestionnarieFormValues
                    | COFAndIndividualQuestionnarieFormValues
                  >
                }
                questionnarieClearErrors={questionnarieClearErrors}
                status={status}
                isEditable={isEditable}
                questionnarieErrors={questionnarieErrors}
                questionnarieFields={questionnarieFields}
                countries={countries}
                relationshipToTheCustomerDictionary={
                  relationshipToTheCustomerDictionary
                }
                questionnarieSetValue={questionnarieSetValue}
                questionnarieIsLoading={questionnarieIsLoading}
              />
              <GroupOfCompanies
                questionnarieControl={questionnarieControl}
                questionnarieClearErrors={questionnarieClearErrors}
                questionnarieErrors={questionnarieErrors}
                status={status}
                isEditable={isEditable}
                countries={countries}
                sourceOfWealth={sourceOfWealth}
                baseCurrency={baseCurrency}
                questionnarieSetValue={questionnarieSetValue}
                questionnarieFields={questionnarieFields}
                questionnarieIsLoading={questionnarieIsLoading}
              />
              <BusinessActivities
                questionnarieControl={questionnarieControl}
                questionnarieClearErrors={questionnarieClearErrors}
                questionnarieErrors={questionnarieErrors}
                status={status}
                isEditable={isEditable}
                busniessActivities={busniessActivities}
                customerMainCleints={customerMainCleints}
                salesChannels={salesChannels}
                questionnarieSetValue={questionnarieSetValue}
                questionnarieFields={questionnarieFields}
                customerSourceOfFunds={customerSourceOfFunds}
                companyLegalName={companyLegalName}
                questionnarieIsLoading={questionnarieIsLoading}
              />
              <CustomerWealth
                questionnarieControl={questionnarieControl}
                questionnarieClearErrors={questionnarieClearErrors}
                questionnarieErrors={questionnarieErrors}
                status={status}
                isEditable={isEditable}
                questionnarieSetValue={questionnarieSetValue}
                questionnarieFields={questionnarieFields}
                annualIncomes={annualIncomes}
                totalIncomeError={totalIncomeError}
                setTotalIncomeError={setTotalIncomeError}
                customerSourceOfWealth={customerSourceOfWealth}
                baseCurrency={baseCurrency}
                questionnarieIsLoading={questionnarieIsLoading}
              />
            </div>
          </StepOneBlockWrapper>
          <StepOneBlockWrapper
            title="Account & Expected Activity"
            regulatedCompanyScheme
          >
            <div className={commonStyles.stepBlockContent}>
              {questionnarieIsLoading && (
                <StepPartBlock withoutTitle>
                  <div className={commonStyles.dictionaryLoader}>
                    <IconLoader />
                  </div>
                </StepPartBlock>
              )}
              {!questionnarieIsLoading && (
                <>
                  <StepPartBlock title="Purpose for establishing business relationship">
                    <InfoRow
                      title="Type of the account"
                      id="accountTypeIds"
                      style={{
                        position: 'relative',
                        zIndex: 7,
                      }}
                      lessPadding
                    >
                      <Controller
                        control={questionnarieControl}
                        name="accountTypeIds"
                        defaultValue={[]}
                        render={({ field: { onChange, value, name } }) => (
                          <Select
                            name={name}
                            placeholder="Select an option..."
                            list={
                              typeOfAccounts?.map((item) => ({
                                name: item.value,
                                value: item.id,
                              })) || []
                            }
                            value={value}
                            size={SelectSize.SMALL}
                            onChange={(e) => {
                              onChange(e);
                              questionnarieClearErrors(['accountTypeIds']);
                            }}
                            selectWrapperClassName={
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED
                                ? commonStyles.selectDisabled
                                : ''
                            }
                            disabled={
                              !isEditable ||
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                            }
                            tabIndex={
                              !isEditable &&
                              Boolean(
                                status === NewApplicationStatus.ACCEPTED ||
                                status === NewApplicationStatus.DECLINED,
                              )
                                ? -1
                                : 0
                            }
                            error={questionnarieErrors?.accountTypeIds?.message}
                            mode={SelectType.MULTIPLE}
                            fullWidth
                            borderless
                          />
                        )}
                      />
                    </InfoRow>
                    <div className={commonStyles.accountsCurrenciesList}>
                      {questionnarieErrors?.questionnairesAccountsCurrencies
                        ?.message && (
                        <div className={commonStyles.questionnarieError}>
                          {
                            questionnarieErrors
                              ?.questionnairesAccountsCurrencies?.message
                          }
                        </div>
                      )}
                      {!accountsCurrenciesIsLoading &&
                        !accountsCurrenciesIsFetching && (
                          <>
                            {accountsCurrenciesValues?.map((item, index) => (
                              <AccountCurrency
                                key={item.id}
                                accountCurrency={item}
                                setAccountsCurrenciesValues={
                                  setAccountsCurrenciesValues
                                }
                                currencies={currencies}
                                index={index}
                                isEditable={isEditable}
                                status={status}
                                accountsCurrencies={accountsCurrenciesValues}
                                onDeleteAccountCurrencies={
                                  onDeleteAccountCurrencies
                                }
                                selectedAccountsCurrencyId={
                                  selectedAccountsCurrencyId
                                }
                                deleteAccountsCurrenciesInProgress={
                                  deleteAccountsCurrenciesInProgress
                                }
                                setAccountsFlowsValues={setAccountsFlowsValues}
                                accountsFlowsValues={accountsFlowsValues}
                              />
                            ))}
                            {currencies &&
                              currencies?.filter((item) =>
                                accountsCurrenciesValues?.every(
                                  (accountCurrency) =>
                                    item.value !== accountCurrency.currencyId,
                                ),
                              )?.length > 0 && (
                                <ButtonSecondary
                                  text="Add currency"
                                  icon={<IconExpand />}
                                  className={`button-white ${commonStyles.addAccountCurrencyButton}`}
                                  size={ButtonSize.SMALL}
                                  onClick={() => {
                                    let nextAccountsCurrencies: AccountsCurrenciesFormValues[] =
                                      [];

                                    if (accountsCurrenciesValues) {
                                      nextAccountsCurrencies = (
                                        [] as AccountsCurrenciesFormValues[]
                                      ).concat(accountsCurrenciesValues);
                                    }

                                    nextAccountsCurrencies.push({
                                      id: `accounts-currency-${uuidv4()}`,
                                      currencyId: '',
                                      plannedTransactionNature: '',
                                      accountOpeningPurpose: '',
                                    });

                                    setAccountsCurrenciesValues(
                                      nextAccountsCurrencies,
                                    );
                                  }}
                                />
                              )}
                          </>
                        )}
                      {(accountsCurrenciesIsLoading ||
                        accountsCurrenciesIsFetching) && (
                        <div className={commonStyles.accountsCurrenciesLoader}>
                          <IconLoader />
                        </div>
                      )}
                    </div>
                  </StepPartBlock>

                  <StepPartBlock title="Expected flows on own account">
                    {!accountsFlowsIsLoading &&
                      !accountsFlowsIsFetching &&
                      accountsFlowsValues &&
                      accountsFlowsValues?.length > 0 && (
                        <div className={commonStyles.expectedFlowsHint}>
                          Currencies selected in the&nbsp;
                          <strong>
                            &quot;Purpose for establishing business
                            relationship&quot;
                          </strong>
                          &nbsp; section
                        </div>
                      )}

                    <div className={commonStyles.accountsCurrenciesList}>
                      {questionnarieErrors?.questionnairesAccountsFlows
                        ?.message && (
                        <div className={commonStyles.questionnarieError}>
                          {
                            questionnarieErrors?.questionnairesAccountsFlows
                              ?.message
                          }
                        </div>
                      )}
                      {(accountsFlowsIsLoading || accountsFlowsIsFetching) && (
                        <div className={commonStyles.accountsCurrenciesLoader}>
                          <IconLoader />
                        </div>
                      )}
                      {!accountsFlowsValues?.length && (
                        <div className={commonStyles.emptyAccountsFlows}>
                          Add currencies in&nbsp;
                          <strong>
                            &quot;Purpose for establishing business
                            relationship&quot;
                          </strong>
                        </div>
                      )}
                      {!accountsFlowsIsLoading &&
                        !accountsFlowsIsFetching &&
                        Object.keys(groupedAccountsFlows)?.map((group) =>
                          groupedAccountsFlows[group]?.map((flow) => (
                            <AccountFlow
                              key={flow.id}
                              accountFlow={flow}
                              accountsFlows={accountsFlowsValues}
                              setAccountsFlowsValues={setAccountsFlowsValues}
                              currencies={currencies}
                              isEditable={isEditable}
                              status={status}
                              currenciesList={currenciesList}
                            />
                          )),
                        )}
                    </div>
                  </StepPartBlock>
                  <StepPartBlock title="Expected origin of funds">
                    <div className={commonStyles.accountsCurrenciesList}>
                      {(expectedFundsOriginsIsLoading ||
                        expectedFundsOriginsIsFetching) && (
                        <div className={commonStyles.accountsCurrenciesLoader}>
                          <IconLoader />
                        </div>
                      )}
                      {!accountsFlowsValues?.length && (
                        <div className={commonStyles.emptyAccountsFlows}>
                          Add currencies in&nbsp;
                          <strong>
                            &quot;Purpose for establishing business
                            relationship&quot;
                          </strong>
                        </div>
                      )}
                      {questionnarieErrors?.questionnairesExpectedFundsOrigins
                        ?.message && (
                        <div className={commonStyles.questionnarieError}>
                          {
                            questionnarieErrors
                              ?.questionnairesExpectedFundsOrigins?.message
                          }
                        </div>
                      )}
                      {accountsFlowsValues &&
                        accountsFlowsValues?.length > 0 &&
                        !expectedFundsOriginsIsLoading &&
                        !expectedFundsOriginsIsFetching && (
                          <>
                            <ExpectedFundsOrigins
                              status={status}
                              isEditable={isEditable}
                              currencies={
                                accountsCurrenciesValues?.map((item) => ({
                                  name: currencies?.find(
                                    (currency) =>
                                      currency.value === item.currencyId,
                                  )?.name as string,
                                  suffix: currencies?.find(
                                    (currency) =>
                                      currency.value === item.currencyId,
                                  )?.suffix as string,
                                  value: item.currencyId,
                                })) || []
                              }
                              deleteExpectedFundsOriginsInProgress={
                                deleteExpectedFundsOriginsInProgress
                              }
                              onDeleteExpectedFundsOrigins={
                                onDeleteExpectedFundsOrigins
                              }
                              selectedExpectedFundsOriginsId={
                                selectedExpectedFundsOriginsId
                              }
                              expectedFundsOriginsValues={
                                incomingExpectedFundsOfOrigins
                              }
                              expectedFundsOriginsValuesList={
                                expectedFundsOriginsValues
                              }
                              setExpectedFundsOriginsValues={
                                setExpectedFundsOriginsValues
                              }
                              countries={countries}
                              type="INCOMING"
                            />
                            <ExpectedFundsOrigins
                              status={status}
                              isEditable={isEditable}
                              currencies={
                                accountsCurrenciesValues?.map((item) => ({
                                  name: currencies?.find(
                                    (currency) =>
                                      currency.value === item.currencyId,
                                  )?.name as string,
                                  suffix: currencies?.find(
                                    (currency) =>
                                      currency.value === item.currencyId,
                                  )?.suffix as string,
                                  value: item.currencyId,
                                })) || []
                              }
                              deleteExpectedFundsOriginsInProgress={
                                deleteExpectedFundsOriginsInProgress
                              }
                              onDeleteExpectedFundsOrigins={
                                onDeleteExpectedFundsOrigins
                              }
                              selectedExpectedFundsOriginsId={
                                selectedExpectedFundsOriginsId
                              }
                              expectedFundsOriginsValues={
                                outgoingExpectedFundsOfOrigins
                              }
                              expectedFundsOriginsValuesList={
                                expectedFundsOriginsValues
                              }
                              setExpectedFundsOriginsValues={
                                setExpectedFundsOriginsValues
                              }
                              countries={countries}
                              type="OUTGOING"
                            />
                          </>
                        )}
                    </div>
                  </StepPartBlock>
                </>
              )}
            </div>
          </StepOneBlockWrapper>
        </>
      )}
    </div>
  );
};

export default NewApplicationCorporateInfoStepOne;
